import { useEffect, useState, type ReactNode } from 'react';
import { QueryClient, type Query } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { abrirAlmacen } from '@/core/offline/almacen';

/**
 * Las lecturas que sobreviven a quedarse sin señal.
 *
 * No se cachea toda la aplicación: sólo lo que hace falta para poder cargar un
 * trabajo parada en el medio del campo. El resto —liquidaciones, cierres,
 * reportes, configuración— se pide cuando hay conexión, y si no hay, no se
 * muestra. Un importe viejo presentado como actual es peor que una pantalla que
 * dice que no pudo cargar.
 */
export const CLAVES_OFFLINE = [
  'clientes',
  'establecimientos',
  'servicios',
  'participantes',
  'defaults-trabajo',
  'trabajos',
] as const;

const esOffline = (query: Query) =>
  typeof query.queryKey[0] === 'string' &&
  (CLAVES_OFFLINE as readonly string[]).includes(query.queryKey[0]);

/** Una semana. Pasado eso conviene volver a pedir todo antes que confiar. */
const VIGENCIA = 7 * 24 * 60 * 60 * 1000;

function crearCliente() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Lo guardado en el dispositivo tiene que sobrevivir a que la pantalla
        // se desmonte, o al volver a abrirla no habría nada que mostrar.
        gcTime: VIGENCIA,
        staleTime: 60_000,
        // Sin señal, reintentar tres veces sólo demora el mensaje de error.
        retry: (intento, error) => {
          if (!navigator.onLine) return false;
          const mensaje = (error as { message?: string })?.message ?? '';
          return intento < 2 && /fetch|network/i.test(mensaje);
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * El puente entre TanStack Query y el almacén del dispositivo.
 *
 * Comparte base con la cola de altas, pero otro depósito: vaciar el caché nunca
 * puede llevarse por delante un trabajo sin sincronizar.
 */
const almacenamiento = {
  getItem: async (clave: string) => {
    const db = await abrirAlmacen();
    const fila = await db.get('cache', clave);
    return (fila?.valor as string | undefined) ?? null;
  },
  setItem: async (clave: string, valor: string) => {
    const db = await abrirAlmacen();
    await db.put('cache', { clave, valor, guardadoEn: Date.now() });
  },
  removeItem: async (clave: string) => {
    const db = await abrirAlmacen();
    await db.delete('cache', clave);
  },
};

export function QueryProvider({ children }: { children: ReactNode }) {
  const [cliente] = useState(crearCliente);
  const [persister] = useState(() =>
    createAsyncStoragePersister({
      storage: almacenamiento,
      key: 'lecturas',
      throttleTime: 1000,
    }),
  );

  // Cuando vuelve la señal, lo cacheado pasa a ser sospechoso: se revalida.
  useEffect(() => {
    const alVolver = () => void cliente.invalidateQueries();
    window.addEventListener('online', alVolver);
    return () => window.removeEventListener('online', alVolver);
  }, [cliente]);

  return (
    <PersistQueryClientProvider
      client={cliente}
      persistOptions={{
        persister,
        maxAge: VIGENCIA,
        dehydrateOptions: {
          // Sólo lo de la lista de arriba, y sólo si la lectura salió bien:
          // guardar un error en el dispositivo lo dejaría fijo hasta la próxima
          // vez que haya señal.
          shouldDehydrateQuery: (query) => query.state.status === 'success' && esOffline(query),
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
